// Sugalarm — WebAuthn browser helpers (cycle 152).
//
// The server speaks WebAuthn-JSON (base64url strings for every binary
// field, via py_webauthn's options_to_json); the browser API speaks
// ArrayBuffers. These codecs bridge the two without relying on the
// not-yet-universal PublicKeyCredential.parseCreationOptionsFromJSON.

export function supportsPasskeys(): boolean {
  return (
    typeof window !== "undefined" &&
    "PublicKeyCredential" in window &&
    typeof navigator.credentials?.create === "function"
  );
}

/** A passkey bound to `rpId` is only reachable from origins inside that
 *  registrable domain — hide the button elsewhere (e.g. the Fly origin
 *  when the RP is the custom domain). */
export function originMatchesRpId(rpId: string | null): boolean {
  if (!rpId) return false;
  const host = window.location.hostname;
  return host === rpId || host.endsWith(`.${rpId}`);
}

function b64urlToBuf(s: string): ArrayBuffer {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const b64 = (s + pad).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
}

function bufToB64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

interface CredDescriptorJson {
  id: string;
  type: string;
  transports?: string[];
}

function decodeDescriptors(
  list: CredDescriptorJson[] | undefined,
): PublicKeyCredentialDescriptor[] | undefined {
  if (!list || list.length === 0) return undefined;
  return list.map((d) => ({
    id: b64urlToBuf(d.id),
    type: "public-key" as const,
    transports: d.transports as AuthenticatorTransport[] | undefined,
  }));
}

/** Server JSON → navigator.credentials.create() options. */
export function decodeCreationOptions(
  json: Record<string, unknown>,
): PublicKeyCredentialCreationOptions {
  const j = json as {
    challenge: string;
    rp: PublicKeyCredentialRpEntity;
    user: { id: string; name: string; displayName: string };
    pubKeyCredParams: PublicKeyCredentialParameters[];
    timeout?: number;
    excludeCredentials?: CredDescriptorJson[];
    authenticatorSelection?: AuthenticatorSelectionCriteria;
    attestation?: AttestationConveyancePreference;
  };
  return {
    challenge: b64urlToBuf(j.challenge),
    rp: j.rp,
    user: {
      id: b64urlToBuf(j.user.id),
      name: j.user.name,
      displayName: j.user.displayName,
    },
    pubKeyCredParams: j.pubKeyCredParams,
    timeout: j.timeout,
    excludeCredentials: decodeDescriptors(j.excludeCredentials),
    authenticatorSelection: j.authenticatorSelection,
    attestation: j.attestation,
  };
}

/** Server JSON → navigator.credentials.get() options. */
export function decodeRequestOptions(
  json: Record<string, unknown>,
): PublicKeyCredentialRequestOptions {
  const j = json as {
    challenge: string;
    rpId?: string;
    timeout?: number;
    allowCredentials?: CredDescriptorJson[];
    userVerification?: UserVerificationRequirement;
  };
  return {
    challenge: b64urlToBuf(j.challenge),
    rpId: j.rpId,
    timeout: j.timeout,
    allowCredentials: decodeDescriptors(j.allowCredentials),
    userVerification: j.userVerification,
  };
}

/** Attestation (create) result → the JSON shape py_webauthn verifies. */
export function encodeAttestation(
  cred: PublicKeyCredential,
): Record<string, unknown> {
  const resp = cred.response as AuthenticatorAttestationResponse;
  const transports =
    typeof resp.getTransports === "function" ? resp.getTransports() : undefined;
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment ?? undefined,
    clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: bufToB64url(resp.clientDataJSON),
      attestationObject: bufToB64url(resp.attestationObject),
      transports,
    },
  };
}

/** Assertion (get) result → the JSON shape py_webauthn verifies. */
export function encodeAssertion(
  cred: PublicKeyCredential,
): Record<string, unknown> {
  const resp = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment ?? undefined,
    clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: bufToB64url(resp.clientDataJSON),
      authenticatorData: bufToB64url(resp.authenticatorData),
      signature: bufToB64url(resp.signature),
      userHandle: resp.userHandle ? bufToB64url(resp.userHandle) : null,
    },
  };
}
